import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
	static targets = ['select', 'input', 'list', 'status'];
	static values = {
		minChars: { type: Number, default: 3 },
		includeEmpty: { type: Boolean, default: false },
		panelSearch: { type: Boolean, default: false },
		panelSearchVisible: { type: Boolean, default: true },
		searchable: { type: Boolean, default: true },
		noResultsText: String,
		minCharsText: String,
		resultsText: String,
	};

		connect() {
			this._handleOutsidePointerDown = this._handleOutsidePointerDown.bind(this);
			this.options = this.getOptions();
			this.activeIndex = -1;
			this.selectTarget.classList.add('sr-only');
			this.selectTarget.setAttribute('tabindex', '-1');
			this.selectTarget.setAttribute('aria-hidden', 'true');
			this.inputTarget.setAttribute('aria-expanded', 'false');
			document.addEventListener('pointerdown', this._handleOutsidePointerDown);
			if (this.panelSearchValue || !this.searchableValue) {
				this.inputTarget.readOnly = true;
				this.inputTarget.setAttribute('aria-readonly', 'true');
			}

			this._normalizeVisualStyle();
			this.syncFromSelect();
		}

	filter(event) {
		this._closeOtherInstances();
		this.refreshOptions();
		const selected = this.options.find((option) => option.value === this.selectTarget.value);
		const isPanelSearch = event?.currentTarget?.hasAttribute('data-searchable-select-panel-search');
		const inputValue = isPanelSearch ? event.currentTarget.value.trim() : this.inputTarget.value.trim();
		const query = !event || (this.panelSearchValue && !isPanelSearch) ? '' : selected?.value === '' && selected.text === inputValue ? '' : inputValue;

		if (query.length < this.minCharsValue) {
			this.activeIndex = -1;
			this.render([], query);
			this.close();
			this.updateStatus(this.minCharsTextValue || `Type at least ${this.minCharsValue} characters.`);
			this.clearSelectionIfInputChanged();
			return;
		}

		const normalizedQuery = this.normalize(query);
		const results = this.options.filter((option) => this.normalize(option.text).includes(normalizedQuery));

		this.activeIndex = -1;
		this.render(results, query);
		this.open();
		this.updateStatus(this.getResultsText(results.length));
		if (!this.panelSearchValue || !isPanelSearch) {
			this.clearSelectionIfInputChanged();
		}
		if (isPanelSearch) {
			this.focusPanelSearch(inputValue);
		} else if (this.panelSearchValue) {
			this.focusPanelSearch(query);
		}
	}

	toggle(event) {
		event.preventDefault();
		if (this.isOpen()) {
			this.close();
			return;
		}

		this.filter();
	}

	closeOnListClick(event) {
		if (event.target?.closest?.('[role="option"]')) {
			this.close();
		}
	}

	handleKeydown(event) {
		let buttons = this.getOptionButtons();

		if (event.key === 'Escape') {
			event.preventDefault();
			this.close();
			return;
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			if (!this.isOpen()) this.filter();
			buttons = this.getOptionButtons();
			this.setActiveIndex(Math.min(this.activeIndex + 1, buttons.length - 1));
			return;
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			this.setActiveIndex(Math.max(this.activeIndex - 1, 0));
			return;
		}

		if (event.key === 'Enter' && this.isOpen() && this.activeIndex >= 0) {
			event.preventDefault();
			buttons[this.activeIndex]?.click();
		}
	}

	selectOption(event) {
		event.preventDefault();
		event.stopPropagation();
		const button = event.currentTarget;
		this.selectTarget.value = button.dataset.value || '';
		this.inputTarget.value = button.dataset.text || '';
		this.dispatchNativeEvent(this.selectTarget, 'change');
		this.inputTarget.focus();
		this.close();
	}

	closeSoon() {
		window.setTimeout(() => {
			if (this.element.contains(document.activeElement)) return;
			this.close();
		}, 150);
	}

	syncFromSelect() {
		this.refreshOptions();
		this.syncDisabled();
		const selected = this.options.find((option) => option.value === this.selectTarget.value);
		if (selected) {
			this.inputTarget.value = selected.text;
		}
	}

	syncDisabled() {
		this.inputTarget.disabled = this.selectTarget.disabled;
	}

	getOptions() {
		return Array.from(this.selectTarget.options)
			.filter((option) => this.includeEmptyValue || option.value)
			.map((option) => ({
				value: option.value,
				text: option.textContent.trim(),
			}));
	}

	refreshOptions() {
		this.options = this.getOptions();
	}

	render(results, query = '') {
		this.listTarget.innerHTML = '';

		const searchMarkup = this.panelSearchValue && this.panelSearchVisibleValue ? this.getPanelSearchMarkup(query) : '';

		if (results.length === 0) {
			this.inputTarget.removeAttribute('aria-activedescendant');
			if (query.length >= this.minCharsValue) {
				this.listTarget.innerHTML = `${searchMarkup}<li class="searchable-select__empty">${this.escapeHtml(
					this.noResultsTextValue || 'No results found.'
				)}</li>`;
			} else {
				this.listTarget.innerHTML = searchMarkup;
			}
			return;
		}

		this.listTarget.innerHTML = searchMarkup + results
			.map(
				(option, index) => `
					<li role="presentation">
						<button type="button"
							class="searchable-select__option"
							role="option"
							id="${this.optionId(index)}"
							data-value="${this.escapeHtml(option.value)}"
							data-text="${this.escapeHtml(option.text)}"
							data-active="${index === this.activeIndex ? 'true' : 'false'}"
							data-action="click->searchable-select#selectOption">
							${this.escapeHtml(option.text)}
						</button>
					</li>
				`
			)
			.join('');

		this.inputTarget.setAttribute('aria-activedescendant', this.optionId(this.activeIndex));
	}

	getPanelSearchMarkup(query) {
		const searchId = `${this.inputTarget.id}-panel-search`;
		const label = this.getSearchLabel();

		return `
			<li class="searchable-select__search" role="presentation">
				<label class="sr-only" for="${searchId}">${this.escapeHtml(label)}</label>
				<input id="${searchId}"
					type="search"
					class="searchable-select__search-input"
					placeholder="${this.escapeHtml(label)}"
					value="${this.escapeHtml(query)}"
					autocomplete="off"
					data-searchable-select-panel-search
					data-action="input->searchable-select#filter keydown->searchable-select#handleKeydown" />
			</li>
		`;
	}

	focusPanelSearch(value) {
		if (!this.panelSearchVisibleValue) return;

		window.requestAnimationFrame(() => {
			const searchInput = this.listTarget.querySelector('[data-searchable-select-panel-search]');
			if (!searchInput) return;
			searchInput.focus();
			searchInput.setSelectionRange(value.length, value.length);
		});
	}

	clearSelectionIfInputChanged() {
		const selected = this.options.find((option) => option.value === this.selectTarget.value);
		if (selected && selected.text === this.inputTarget.value.trim()) {
			return;
		}

		if (this.selectTarget.value) {
			this.selectTarget.value = '';
			this.dispatchNativeEvent(this.selectTarget, 'change');
		}
	}

	setActiveIndex(index) {
		const buttons = this.getOptionButtons();
		if (buttons.length === 0) {
			this.activeIndex = -1;
			this.inputTarget.removeAttribute('aria-activedescendant');
			return;
		}

		this.activeIndex = Math.max(0, Math.min(index, buttons.length - 1));
		buttons.forEach((button, buttonIndex) => {
			button.dataset.active = buttonIndex === this.activeIndex ? 'true' : 'false';
		});
		buttons[this.activeIndex]?.scrollIntoView({ block: 'nearest' });
		this.inputTarget.setAttribute('aria-activedescendant', this.optionId(this.activeIndex));
	}

	getOptionButtons() {
		return Array.from(this.listTarget.querySelectorAll('[role="option"]'));
	}

	open() {
		this._closeOtherInstances();
		this.element.style.zIndex = '60';
		this.listTarget.hidden = false;
		this.inputTarget.setAttribute('aria-expanded', 'true');
		// Flatten the bottom of the input so the open panel reads as nested under it.
		this.inputTarget.classList.add('rounded-b-none');
	}

		disconnect() {
			document.removeEventListener('pointerdown', this._handleOutsidePointerDown);
		}

		_normalizeVisualStyle() {
			if (!this.hasListTarget) return;

			// Keep the list as an overlay so opening the select never shifts page content.
			this.listTarget.classList.remove('relative', 'mt-2');
			this.listTarget.classList.add('absolute', 'left-0', 'right-0', 'top-full', 'z-20');
		}

	_closeOtherInstances() {
		document.querySelectorAll('[data-controller~="searchable-select"]').forEach((el) => {
			if (el !== this.element) {
				const input = el.querySelector('[data-searchable-select-target="input"]');
				const list = el.querySelector('[data-searchable-select-target="list"]');
				if (input) {
					input.setAttribute('aria-expanded', 'false');
					input.classList.remove('rounded-b-none');
					input.removeAttribute('aria-activedescendant');
				}
				if (list) {
					list.hidden = true;
				}
				el.style.zIndex = '';
			}
		});
	}

	_handleOutsidePointerDown(event) {
		if (!this.element.contains(event.target)) {
			this.close();
		}
	}

	close() {
		this.element.style.zIndex = '';
		this.listTarget.hidden = true;
		this.inputTarget.setAttribute('aria-expanded', 'false');
		this.inputTarget.removeAttribute('aria-activedescendant');
		this.inputTarget.classList.remove('rounded-b-none');
	}

	isOpen() {
		return this.inputTarget.getAttribute('aria-expanded') === 'true';
	}

	updateStatus(text) {
		if (this.hasStatusTarget) {
			this.statusTarget.textContent = text;
		}
	}

	getResultsText(count) {
		const template = this.resultsTextValue || '{0} results found.';
		return template.replace('{0}', count);
	}

	getSearchLabel() {
		return document.documentElement.dataset.filterDropdownSearchLabel || 'Search';
	}

	optionId(index) {
		return `${this.inputTarget.id}-option-${index}`;
	}

	normalize(value) {
		return value
			.toLocaleLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '');
	}

	escapeHtml(value) {
		return String(value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');
	}

	dispatchNativeEvent(element, eventName) {
		element.dispatchEvent(new Event(eventName, { bubbles: true }));
	}
}
